/**
 * @fileoverview Dashboard organizer page component.
 * @summary Main dashboard page for organizers to manage their events.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/DashboardOrganizerPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.js'
import { fetchEvents, setFilterStatus, hideEvent, type EventStatus, type Event } from '../../store/slices/eventsSlice.js'
import PlanLimitAlert from '../../components/PlanLimitAlert.js'
import MonthlyQuotaDisplay from '../../components/MonthlyQuotaDisplay.js'
import { getOrganizerLimits, canCreateEvent, getBillingPeriod } from '../../utils/planLimits.js'
import { ConfirmModal } from '../../components/ConfirmModal.js'
import PageHeader from '../../components/PageHeader.js'
import { EventFiltersBar } from './components/EventFiltersBar.js'
import { EventListSection } from './components/EventListSection.js'
import QuotaExceededCreateModal from './components/QuotaExceededCreateModal.js'
import { SubscriptionType } from '@karaonow/shared'

/**
 * @summary Dashboard organizer page component.
 * @returns {React.ReactElement} Dashboard organizer page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function DashboardOrganizerPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const { events, isLoading, error, filterStatus } = useAppSelector((state) => state.events)

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    eventId: string | null
    eventName: string
  }>({
    isOpen: false,
    eventId: null,
    eventName: '',
  })
  const [isHiding, setIsHiding] = useState<string | null>(null)
  const [includeHidden, setIncludeHidden] = useState(false)
  const [allEventsForQuota, setAllEventsForQuota] = useState<Event[]>([])
  const [showQuotaModal, setShowQuotaModal] = useState(false)
  const [quotaErrorData, setQuotaErrorData] = useState<{
    currentCount?: number
    limit?: number
    userPlan?: string
  } | null>(null)

  // Redirect if not authenticated or not organizer
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (user.role !== 'organizer' && user.role !== 'superadmin') {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  // Fetch events on mount and when includeHidden changes
  useEffect(() => {
    if (user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchEvents(includeHidden))
    }
  }, [dispatch, user, includeHidden])

  // Always fetch all events (including hidden) for quota calculation
  useEffect(() => {
    if (user && (user.role === 'organizer' || user.role === 'superadmin')) {
      dispatch(fetchEvents(true))
        .unwrap()
        .then((allEvents: Event[]) => {
          setAllEventsForQuota(allEvents)
        })
        .catch((err) => {
          console.error('Failed to fetch all events for quota:', err)
        })
    }
  }, [dispatch, user])

  // Filter events by status (exclude hidden by default unless includeHidden is true)
  const filteredEvents = events.filter((event) => {
    // Exclude hidden events unless includeHidden is true
    if (!includeHidden && event.status === 'hidden') return false
    if (filterStatus === 'all') return true
    return event.status === filterStatus
  })

  const handleFilterChange = (status: EventStatus | 'all') => {
    dispatch(setFilterStatus(status))
  }

  const handleHideClick = (event: Event) => {
    // Only completed or cancelled events can be hidden
    if (event.status !== 'completed' && event.status !== 'cancelled') {
      return
    }
    setConfirmModal({
      isOpen: true,
      eventId: event.id,
      eventName: event.name,
    })
  }

  const handleConfirmHide = async () => {
    if (!confirmModal.eventId) return

      setIsHiding(confirmModal.eventId)
    try {
      await dispatch(hideEvent(confirmModal.eventId)).unwrap()
      setConfirmModal({ isOpen: false, eventId: null, eventName: '' })
      // Refresh events list to update the UI
      dispatch(fetchEvents(includeHidden))
      // Refresh all events for quota calculation
      dispatch(fetchEvents(true))
        .unwrap()
        .then((allEvents: Event[]) => {
          setAllEventsForQuota(allEvents)
        })
        .catch((err) => {
          console.error('Failed to refresh all events for quota:', err)
        })
    } catch (err) {
      // Error is already in state
      console.error('Failed to hide event:', err)
      setConfirmModal({ isOpen: false, eventId: null, eventName: '' })
    } finally {
      setIsHiding(null)
    }
  }

  const handleCancelHide = () => {
    setConfirmModal({ isOpen: false, eventId: null, eventName: '' })
  }

  const handleCreateEvent = () => {
    if (!user) return

    // Get organizer limits
    const organizerLimits = getOrganizerLimits(user)
    
    // Calculate current events count based on plan type
    let currentEventsCount: number
    
    if (organizerLimits.isMonthlyQuota) {
      // For PRO/PREMIUM: Count events in current billing period
      const billingPeriod = getBillingPeriod(user)
      const eventsToCount = allEventsForQuota.length > 0 ? allEventsForQuota : events
      
      if (billingPeriod) {
        currentEventsCount = eventsToCount.filter((e) => {
          const eventDate = new Date(e.date)
          const inPeriod = eventDate >= billingPeriod.start && eventDate <= billingPeriod.end
          return inPeriod && e.planType === 'subscription'
        }).length
      } else {
        // Fallback: count all subscription events
        currentEventsCount = eventsToCount.filter((e) => e.planType === 'subscription').length
      }
    } else {
      // For FREE: Count all FREE events
      const eventsToCount = allEventsForQuota.length > 0 ? allEventsForQuota : events
      currentEventsCount = eventsToCount.filter((e) => 
        !e.planType || e.planType === SubscriptionType.FREE
      ).length
    }

    // Check if user can create event
    const quotaCheck = canCreateEvent(user, currentEventsCount)
    
    if (!quotaCheck.canCreate) {
      // Show quota exceeded modal
      setQuotaErrorData({
        currentCount: quotaCheck.currentCount,
        limit: quotaCheck.limit,
        userPlan: user.subscription?.type || 'free',
      })
      setShowQuotaModal(true)
      return
    }

    // Quota OK, navigate to create page
    navigate('/events/create')
  }

  if (!user || (user.role !== 'organizer' && user.role !== 'superadmin')) {
    return null
  }

  // Get organizer limits (centralized logic)
  const organizerLimits = getOrganizerLimits(user)
  const userIsFreePlan = organizerLimits.userPlan === SubscriptionType.FREE
 

  // Count only events that count in quota (conditional logic)
  // Use allEventsForQuota (includes hidden events) for accurate quota calculation
  const quotaEventsCount = (allEventsForQuota.length > 0 ? allEventsForQuota : events).filter((e) => {
    if (organizerLimits.isMonthlyQuota) {
      // Pour PRO/PREMIUM : compter uniquement 'subscription'
      return e.planType === 'subscription'
    } else {
      // Pour FREE : compter uniquement 'free'
      return !e.planType || e.planType === SubscriptionType.FREE
    }
  }).length

  const userHasReachedLimit = organizerLimits.maxEvents <= quotaEventsCount
//   console.log('Test')
// console.log('organizerLimits', organizerLimits)
  return (
    <div className="min-h-screen ">
      <div className="w-full page-container">
        <PageHeader
          title={t('dashboard.title')}
          subtitle={user.profile?.firstName ? `${t('dashboard.welcome')}, ${user.profile.firstName}!` : undefined}
        />

        {/* Monthly Quota Display for PRO/PREMIUM plans */}
        {((!userHasReachedLimit && userIsFreePlan) || (organizerLimits.isMonthlyQuota)) && (
          <MonthlyQuotaDisplay
            user={user}
            events={allEventsForQuota.length > 0 ? allEventsForQuota : events}
            onUpgrade={() => navigate('/subscription')}
            className="mb-6"
          />
        )}

        {/* Plan Limit Alert for Events - Only for FREE plan users */}
        {organizerLimits.hasEventLimit && userIsFreePlan && (
          <PlanLimitAlert
            limitReached={quotaEventsCount >= organizerLimits.maxEvents}
            currentCount={quotaEventsCount}
            limit={organizerLimits.maxEvents}
            limitType="events"
            onUpgrade={() => navigate('/subscription')}
            className="mb-6"
          />
        )}

        {/* Filters */}
        <EventFiltersBar
          currentFilter={filterStatus}
          onFilterChange={handleFilterChange}
          includeHidden={includeHidden}
          onIncludeHiddenChange={setIncludeHidden}
        />

        {/* Create Event Button - Show if there are events (not in empty state) */}
        {events.length > 0 && (
          <div className="mb-8">
            <button
              type="button"
              onClick={handleCreateEvent}
              className="btn btn-primary btn-lg"
            >
              {t('dashboard.createEvent')}
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-6">
            <div className="text-sm font-medium">{error}</div>
          </div>
        )}

        {/* Events List */}
        <EventListSection
          isLoading={isLoading}
          events={events}
          filteredEvents={filteredEvents}
          hasEvents={events.length > 0}
          onDeleteClick={handleHideClick}
          onCreateEvent={handleCreateEvent}
        />

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={t('events.hideConfirmTitle') || 'Masquer l\'événement'}
          message={t('events.hideConfirm')?.replace('{{name}}', confirmModal.eventName) || `Masquer l'événement "${confirmModal.eventName}" ? Il ne sera plus visible dans votre liste principale, mais restera comptabilisé dans votre quota mensuel.`}
          confirmLabel={t('events.hide') || 'Masquer'}
          cancelLabel={t('common.cancel') || 'Annuler'}
          variant="warning"
          onConfirm={handleConfirmHide}
          onCancel={handleCancelHide}
          isLoading={confirmModal.eventId ? isHiding === confirmModal.eventId : false}
        />

        {/* Quota Exceeded Modal */}
        {quotaErrorData && (
          <QuotaExceededCreateModal
            isOpen={showQuotaModal}
            currentCount={quotaErrorData.currentCount}
            limit={quotaErrorData.limit}
            userPlan={quotaErrorData.userPlan}
            onCancel={() => {
              setShowQuotaModal(false)
              setQuotaErrorData(null)
            }}
            onCreateOneShot={() => {
              // Navigate to subscription page where user can create ONE_SHOT event
              navigate('/subscription')
              setShowQuotaModal(false)
              setQuotaErrorData(null)
            }}
          />
        )}
      </div>
    </div>
  )
}

